import { useEffect, type ReactNode } from 'react';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { IconChevronLeft } from '../icons';
import { useLanding, type Landing } from './useLanding';

/**
 * Detail-page anatomy — SONAR's, adopted exactly (PRD v2 §3): back-crumb →
 * h1 + inline status badge → meta strip → sectioned cards.
 */

/**
 * Keep an addressed card in place while the page above it finishes loading.
 *
 * The router scrolls to `#hash` ONCE, on the first commit. On a slow connection the cards
 * above the target are still skeletons at that moment, and they grow — so the card the link
 * named slides down and out of view. In Chromium and Gecko the browser's own CSS scroll
 * anchoring absorbs that (measured: 400px inserted above a landed anchor moved it 0px).
 * **WebKit does not implement scroll anchoring**, so on iOS the drift is real, and "visible
 * on arrival" is the whole point of the anchor.
 *
 * So: re-align on layout changes until the page is quiet, and get out of the way the instant
 * the reader does anything. Deliberately narrow —
 *
 * - It gives up on the FIRST wheel, touch, pointer or key event. Scrolling a page that
 *   fights back is worse than landing slightly wrong, and a reader who has started moving
 *   has already stopped caring where the link pointed.
 * - It stops after 2s regardless. A page that never settles must not be re-scrolled forever.
 * - It does nothing at all without a hash, and nothing if the element is missing.
 *
 * Called by the page, not by `SectionCard` — ninety-one cards do not need ninety-one
 * observers, and the page is what knows it has anchors.
 */
export function useHashAnchor(): void {
  const hash = useRouterState({ select: (s) => s.location.hash });

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash);
    if (!el) return;

    let live = true;
    let frame = 0;
    const release = (): void => {
      if (!live) return;
      live = false;
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      for (const e of SURRENDER) window.removeEventListener(e, release);
    };

    /**
     * Where the card should sit: its own `scroll-margin-top`, which is the whole point of the
     * offset. Read once, from the element, so this cannot disagree with the CSS.
     */
    const target = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;

    /**
     * A frame loop, NOT a `ResizeObserver` on the body.
     *
     * 🔴 The first version observed `document.body` and never fired once: `theme.css` sets
     * `html, body { height: 100% }`, so the body box is the viewport and does not grow when
     * content is added below it. The observer was live, the callback ran exactly once on
     * `observe()`, and the card drifted 424px with every assertion about the mechanism
     * looking healthy. Only a negative control caught it.
     */
    const tick = (): void => {
      if (!live) return;
      if (Math.abs(el.getBoundingClientRect().top - target) > 1) {
        el.scrollIntoView({ block: 'start' });
      }
      frame = requestAnimationFrame(tick);
    };

    const timer = setTimeout(release, 2000);
    frame = requestAnimationFrame(tick);
    for (const e of SURRENDER) window.addEventListener(e, release, { passive: true });

    return release;
  }, [hash]);
}

/** Any of these means the reader has taken over, and this hook is finished. */
const SURRENDER = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
export function BackCrumb({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-text"
    >
      <IconChevronLeft size={15} />
      {label}
    </Link>
  );
}

export function DetailHeader({
  title,
  badge,
  right,
}: {
  title: ReactNode;
  badge?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <h1 className="font-heading text-2xl font-bold">{title}</h1>
        {badge}
      </div>
      {/* The actions WRAP: a sent quote carries six (PDF, Send again, They said no, They said
          yes, Revise, Save as template), and in one row that could not break they pushed
          `<main>` ~105 px sideways at 390 px (MAP 249 QA D5). `min-w-0` lets the row shrink
          beside the title on a wider window too, rather than overflow it. */}
      {right ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2">{right}</div>
      ) : null}
    </div>
  );
}

/** Inline meta facts under the h1 — label · value pairs separated by dots. */
export function MetaStrip({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
      {items.map((m, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          <span>{m.label}</span>
          <span className="font-medium text-text">{m.value}</span>
        </span>
      ))}
    </div>
  );
}

interface SectionCardProps {
  /**
   * Makes the card addressable: `/settings#hourly-cost` lands on it. The router already
   * does `document.getElementById(hash).scrollIntoView()` — the only thing missing was an
   * element for it to find, which is why one optional prop here addresses all fifteen
   * settings cards and every detail-page card at once.
   *
   * ⚠️ IT LOOKS ONCE. The lookup runs in a layout effect on the first commit after the
   * route subtree mounts and is never retried, so a card that renders `null` until its
   * query resolves has no anchor at the only moment anyone asks for one. Render the shell
   * first and put the skeleton inside it. `settings-anchors.test.ts` fails on a `null`
   * return guarded by `isPending`, because that is how this shipped broken the first time.
   *
   * ⚠️ Scroll restoration competes with it, but NARROWLY. In `scroll-restoration.ts` the
   * hash branch is `if (!windowRestored && hash && …)`, and `windowRestored` needs both
   * `createRouter({ scrollRestoration })` and a cached position that actually gets restored.
   * An ordinary PUSH to an anchored URL still lands — `skipWindowRestore` covers exactly
   * that case. What it would break is going BACK to one. It is off today and the guard suite
   * pins that. *(An earlier version of this comment said it would kill every anchor on load.
   * That was wrong, an adversary checked the vendored source, and I first corrected it in
   * the test and left this copy standing — one of N parallel paths, again.)*
   *
   * ⚠️ A card that can mount MORE THAN ONCE on a page must not take one. Two elements
   * sharing a DOM id is invalid HTML and `getElementById` silently resolves to whichever
   * rendered first — so the link would work, land on the wrong card, and look correct.
   * The composer owns the anchor namespace for exactly this reason; no card hardcodes
   * an id it might carry to a second mount.
   */
  id?: string;
  title: string;
  right?: ReactNode;
  children: ReactNode;
}

export function SectionCard({ id, title, right, children }: SectionCardProps) {
  /*
   * ⭐ An addressed card is LANDED ON like every other deep link (UX baseline): scrolled to,
   * then marked for a moment (`useLanding`, the mark every landing wears). Before, a card's
   * link (`/settings#email` from Connected services) scrolled and left nothing to find on
   * arrival, while the controls inside that same card were marked (MAP 249 QA D6).
   *
   * Landing reads the URL's hash from the router, which every page of the app has. A card
   * rendered on its own (to markup, by a test) has none, and is the plain card: nothing to
   * land on without a URL.
   */
  const router = useRouter({ warn: false });
  return id && router ? (
    <LandedCard id={id} title={title} right={right}>
      {children}
    </LandedCard>
  ) : (
    <CardFrame id={id} title={title} right={right}>
      {children}
    </CardFrame>
  );
}

/** An addressed card on a routed page: landed on while the URL's hash names it. */
function LandedCard({ id, title, right, children }: SectionCardProps & { id: string }) {
  const landing = useLanding<HTMLElement>(id);
  return (
    <CardFrame id={id} title={title} right={right} landing={landing}>
      {children}
    </CardFrame>
  );
}

/** The card itself: the one element that carries its id. */
function CardFrame({
  id,
  title,
  right,
  children,
  landing,
}: SectionCardProps & { landing?: Landing<HTMLElement> }) {
  return (
    <section
      id={id}
      ref={landing?.ref}
      data-landed={landing?.lit ? 'true' : undefined}
      /*
       * Landing on the card is not enough — it has to be VISIBLE on arrival. `scrollIntoView`
       * aligns the section's top with the viewport's, and OfficeShell's mobile bar is
       * `sticky top-0 z-30`, so without this the bar covers the heading it just scrolled to.
       * The offset travels with the card rather than living on the page, so every card in
       * the app inherits it. Measured, not guessed: the bar is 53px at 390px.
       *
       * ⚠️ This positions the card at the moment of the scroll. HOLDING it there while the
       * cards above finish loading is `useHashAnchor`, above — because the browser only does
       * it for you in Chromium and Gecko, and WebKit ships no CSS scroll anchoring at all.
       *
       * The mark REPLACES the surface while it is lit: of two background utilities on one
       * element, the stylesheet's order decides, not the class list's.
       */
      className={`scroll-mt-[var(--sw-anchor-offset)] rounded-[var(--radius)] border border-border transition-colors ${landing?.lit ? landing.mark : 'bg-surface'}`}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}
